<template>
    <div
        ref="table"
        :class="[customScrollClass, {'data-table-scroll': !customScrollClass, 'min-h-[20rem]': isLoading}]"
        :style="gridStyle"
        style="height: auto; max-height: 90vh;"
        class="data-table overflow-y-auto text-sm text-gray-600 grid grid-flow-col bg-gray-100 dark:bg-gray-600 dark:text-gray-300"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <template
            v-if="collection"
        >
            <initial-column
                :has-actions="hasActions"
                :rows="rows"
                @update:categories-open="setCategoriesOpen($event)"
            >
                <template #title>
                    <slot name="title"/>
                </template>
                <template #actions>
                    <slot name="title-actions"/>
                </template>
            </initial-column>

            <template v-for="(column, index) in collection.columns">
                <column
                    :key="'collection_'+index"
                    :actions="actions"
                    :class="{'border-r': (additionalColumns && additionalColumns.columns.length > 0) || index < collection.columns.length - 1}"
                    :column="column"
                    :delete-modal-content="deleteModalContent"
                    :delete-modal-title="deleteModalTitle"
                    :has-actions="hasActions"
                    :rows="rows"
                    :is-import-modal="isImportModal"
                    :tabindex="index"
                >
                    <template v-slot:actions="slot">
                        <slot name="column-actions" :actions="slot.actions" :column="slot.column"
                              :index="slot.tabindex"
                        ></slot>
                    </template>
                </column>
            </template>

            <template v-if="additionalColumns && additionalColumns.columns.length > 0">
                <template v-for="(column, index) in additionalColumns.columns">
                    <column
                        :key="'extra_'+index"
                        :actions="actions"
                        :class="[{'border-r': index < additionalColumns.columns.length - 1}, index >= additionalColumns.columns.length - fixedAdditionalColumnsCount ? 'sticky top-0 border-l right-0' : null]"
                        :column="column"
                        :has-actions="hasActions"
                        :rows="rows"
                        :is-import-modal="isImportModal"
                        :tabindex="collection.columns.length"
                    />
                </template>

                <null-column
                    :rows="rows"
                    :has-actions="hasActions"
                />
            </template>
        </template>

        <loading-overlay :is-loading="isLoading"/>
    </div>
</template>

<script>
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import DataColumnActions from '@/Components/Storemanager/Entities/DataColumnActions';
import FieldCategory from '@/Models/FieldCategory';
import InitialColumn from '@/Components/Storemanager/InitialColumn';
import Column from '@/Components/Storemanager/Column';
import NullColumn from '@/Components/Storemanager/NullColumn';

export default {
    components: {NullColumn, Column, InitialColumn},

    props: {
        collection: {
            type: DataColumnCollection,
        },
        additionalColumns: {
            type: DataColumnCollection,
            default: null,
        },
        fixedAdditionalColumnsCount: {
            type: Number,
            default: 1,
        },

        actions: {
            type: DataColumnActions,
            default: () => new DataColumnActions(),
        },

        deleteModalTitle: {
            type: String,
            default: null,
        },
        deleteModalContent: {
            type: String,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isImportModal: {
            type: Boolean,
            default: false,
            required: false
        },

        rowSet: {
            type: Array,
            default: null,
        },
        customScrollClass: {
            type: String,
            default: null,
        },
        withScrollSync: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            rows: [],
        };
    },

    created() {
        if (this.rowSet) {
            this.rows = this.rowSet;
        } else {
            this.rows = storeManager.rows;
        }
    },

    methods: {
        toggleCategoryOpen(category) {
            category.isOpen = !category.isOpen;
        },
        setCategoriesOpen(status) {
            this.rows.forEach(entry => {
                if (entry instanceof FieldCategory) {
                    entry.isOpen = status;
                }
            });
        },
        isCategory(row) {
            return row instanceof FieldCategory;
        },
        onScroll(event) {
            // Only works on desktop right now
            let target = event.target;

            if (!this.overviewTables || this.overviewTables.length === 0) {
                this.overviewTables = document.querySelectorAll(
                    this.customScrollClass ? '.' + this.customScrollClass : '.data-table-scroll');
            }

            let maxScrollLeft = target.scrollWidth - target.clientWidth;
            let scrollPerPixel = target.scrollLeft / maxScrollLeft;

            this.overviewTables.forEach(el => {
                if (el === target) {
                    return;
                }

                el.scrollLeft = scrollPerPixel * (el.scrollWidth - el.clientWidth);
            });
        },
        onMouseEnter() {
            if (this.withScrollSync) {
                this.$refs.table.addEventListener('scroll', this.onScroll);
                this.$refs.table.addEventListener('touch', this.onScroll);
            }
        },
        onMouseLeave() {
            if (this.withScrollSync) {
                this.$refs.table.removeEventListener('scroll', this.onScroll);
                this.$refs.table.removeEventListener('touch', this.onScroll);
            }
        },
    },

    computed: {
        gridStyle() {
            if (!this.collection) {
                return {};
            }

            let rowCount = this.hasActions ? 2 : 1;

            this.rows.forEach(row => {
                if (!this.isCategory(row)) {
                    if (this.auth.user.is_admin || !row.is_admin_only) {
                        ++rowCount;
                    }
                } else {
                    ++rowCount;

                    if (row.isOpen) {
                        rowCount += row.filteredFields.length;
                    }
                }
            });

            return {
                'grid-template-columns': `repeat(${this.collection.columns.length + (this.additionalColumns ? this.additionalColumns.columns.length : 0) + 4}, 1fr)`,
                'grid-template-rows': `repeat(${rowCount}, auto)`,
            };
        },
        hasActions() {
            return this.collection && !!this.collection.columns.find(column => column.isLocked === false);
        },
    },

    watch: {
        rowSet() {
            if (this.rowSet) {
                this.rows = this.rowSet;
            } else {
                this.rows = storeManager.rows;
            }
        },
    },
};
</script>

<style scoped>
th, td {
    width: 1%;
    min-width: 15rem;
    white-space: nowrap;
}
</style>
